// Shared presentation helpers for the AICMO `actions_feed` payload, used by
// both the in-app Actions Feed panel (AppScanner) and the public report tab.
//
// Payload shape (every field may be missing, and on gated reports any field
// may be a blurred-field descriptor instead of real content):
//   { source, feed_status, summary: { total, problems, advantages },
//     feed: [{ problem, title, description, category, priority, evidence[],
//              solution: { steps[], expected_impact, owner, effort,
//                          timeframe, success_signal } }] }
import { Star, Bug, Search, Store, Users, Megaphone, DollarSign, Sparkles } from "lucide-react"

const isLocked = value => !!value && typeof value === "object" && value.blurred === true
const lower = value => (typeof value === "string" ? value.toLowerCase().trim() : "")

export const PRIORITY_RANK = { high: 0, medium: 1, low: 2 }

export const PRIORITY_COLORS = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#3b82f6",
  advantage: "#10b981",
}

// Category → short label + icon. Unknown categories fall back to a
// title-cased version of the raw value so new backend categories still read
// sensibly without a frontend change.
const CATEGORY_META = {
  ratings: { label: "Ratings", icon: Star },
  reviews: { label: "Reviews", icon: Star },
  bugs: { label: "Gameplay", icon: Bug },
  seo: { label: "SEO", icon: Search },
  aso: { label: "ASO", icon: Search },
  store_listing: { label: "Store listing", icon: Store },
  community: { label: "Community", icon: Users },
  marketing: { label: "Marketing", icon: Megaphone },
  monetization: { label: "Monetization", icon: DollarSign },
  revenue: { label: "Revenue", icon: DollarSign },
}

export const getCategoryMeta = category => {
  const key = lower(category)
  if (!key) return null
  if (CATEGORY_META[key]) return CATEGORY_META[key]
  const label = key.replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase())
  return { label, icon: Sparkles }
}

export const getPriorityKey = item => {
  if (!item?.problem) return "advantage"
  const p = lower(item.priority)
  return PRIORITY_RANK[p] != null ? p : "medium"
}

// 1–3 used to draw the effort meter; null when unknown.
export const getEffortLevel = effort => {
  const e = lower(effort)
  if (e === "low") return 1
  if (e === "medium") return 2
  if (e === "high") return 3
  return null
}

// Problems first (high → low priority), then advantages. Locked items sink to
// the end so a visible item is never pushed out of view by a lock. Stable
// for equal ranks so the API's own ordering is preserved within a group.
export const sortActions = items =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const rank = x => {
        if (isLocked(x)) return 99
        if (!x?.problem) return 10
        return PRIORITY_RANK[lower(x.priority)] ?? 1
      }
      return rank(a.item) - rank(b.item) || a.index - b.index
    })
    .map(({ item }) => item)

// Stable per-item key for persisting step progress — titles are unique per
// feed in practice and survive a re-scan, unlike array indexes.
// Unique per item in the sorted feed: titles alone can repeat (an issue and a
// win may share one), so the position and kind are part of the key.
export const getActionKey = (item, index) => {
  const kind = item?.problem ? "issue" : "win"
  const title = (typeof item?.title === "string" && item.title.trim()) || "untitled"
  return `${index}:${kind}:${title}`
}

// Counts derived from the feed itself; the API `summary` wins when present
// and not locked, so the header always agrees with what the backend reports.
export const getActionsSummary = (actionsFeed, items) => {
  const visible = items.filter(i => !isLocked(i))
  const derived = {
    total: items.length,
    problems: visible.filter(i => i?.problem).length,
    advantages: visible.filter(i => !i?.problem).length,
    highPriority: visible.filter(i => i?.problem && lower(i.priority) === "high").length,
  }
  const s = actionsFeed?.summary || {}
  const pick = (key, fallback) => (typeof s[key] === "number" ? s[key] : fallback)
  return {
    total: pick("total", derived.total),
    problems: pick("problems", derived.problems),
    advantages: pick("advantages", derived.advantages),
    highPriority: derived.highPriority,
  }
}

// "ready" | "pending" | "failed" — anything unrecognised is treated as ready
// so an unexpected value never hides real data.
export const getFeedStatus = actionsFeed => {
  const s = lower(actionsFeed?.feed_status)
  if (["pending", "processing", "queued", "generating", "in_progress"].includes(s)) return "pending"
  if (["failed", "error"].includes(s)) return "failed"
  return "ready"
}
